import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { monthOptions, monthLabel } from "@/lib/dpc";
import { SVC_KINDS, kindLabel, fmtWhen, fmtDay, fmtDealers } from "@/lib/service";
import { ServiceCompliance } from "@/components/ServiceCompliance";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, X, AlertTriangle, CheckCircle2, Undo2, Loader2 } from "lucide-react";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground whitespace-nowrap";

function DropZone({ files, setFiles, busy }) {
  const [drag, setDrag] = useState(false);
  const add = (list) => {
    const incoming = Array.from(list || []).filter((f) => /\.(xlsx|xls|csv)$/i.test(f.name));
    setFiles((prev) => [...prev.filter((p) => !incoming.some((n) => n.name === p.name)), ...incoming]);
  };
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
      className={`rounded-md border-2 border-dashed p-6 text-center transition-colors duration-200 ${drag ? "border-foreground bg-secondary" : "border-border"}`}
      data-testid="svc-dropzone"
    >
      <UploadCloud size={22} className="mx-auto text-muted-foreground" />
      <p className="text-sm mt-2">Drop all 4 DMS reports here — <label className="underline cursor-pointer font-medium">browse<input type="file" multiple accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="svc-file-input" disabled={busy} /></label></p>
      <p className="text-[11px] text-muted-foreground mt-1">GST RO Billing · RO List · Warranty · Sales — the type is recognised from the column headers, not the file name. Files for several workshops can be mixed.</p>
      {files.length > 0 && (
        <ul className="mt-4 flex flex-wrap justify-center gap-2" data-testid="svc-file-list">
          {files.map((f) => (
            <li key={f.name} className="inline-flex items-center gap-1.5 rounded border bg-card px-2 py-1 text-xs" data-testid="svc-file-chip">
              <FileSpreadsheet size={12} className="text-muted-foreground" /> {f.name}
              <button onClick={() => setFiles(files.filter((x) => x !== f))} className="text-muted-foreground hover:text-red-600" aria-label="remove"><X size={12} /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function FileResult({ f }) {
  if (!f.kind) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800" data-testid="svc-file-result-error">
        <b>{f.file_name}</b> — {f.error}
      </div>
    );
  }
  const ok = !f.rows_rejected && !f.unattributed;
  return (
    <div className="rounded-md border bg-card px-3 py-2 text-xs space-y-1" data-testid={`svc-file-result-${f.kind}`}>
      <div className="flex flex-wrap items-center gap-2">
        {ok ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-amber-600" />}
        <span className="rounded bg-foreground text-background px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">{f.kind_label}</span>
        <b className="truncate">{f.file_name}</b>
        {f.group_file && <span className="rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider" data-testid="svc-group-file-badge">group file</span>}
        <span className="text-muted-foreground" title={f.dealers.join(", ")}>{fmtDealers(f.dealers, f.group_file) || "no workshop identified"}</span>
        {f.data_from && <span className="text-muted-foreground">· data {fmtDay(f.data_from)} → {fmtDay(f.data_to)}</span>}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <span className="rounded bg-secondary px-1.5 py-0.5">Rows {f.rows_total}</span>
        <span className="rounded bg-emerald-100 text-emerald-800 px-1.5 py-0.5">Loaded {f.rows_ok}</span>
        {f.rows_rejected > 0 && <span className="rounded bg-red-100 text-red-700 px-1.5 py-0.5">Rejected {f.rows_rejected}</span>}
        {f.units > 0 && <span className="rounded bg-secondary px-1.5 py-0.5">{f.units} {f.unit_label}{f.replaced ? ` · ${f.replaced} replaced` : ""}{f.duplicates ? ` · ${f.duplicates} duplicate row${f.duplicates === 1 ? "" : "s"} collapsed` : ""}</span>}
        {f.rows_total === 0 && <span className="rounded bg-secondary px-1.5 py-0.5 text-muted-foreground">empty report (header only) — counted for compliance</span>}
      </div>
      {f.workshops_absent?.length > 0 && <p className="text-muted-foreground" data-testid="svc-workshops-absent">No records in this extract for {f.workshops_absent.join(", ")} — still counted as received for today's compliance.</p>}
      {f.unattributed && <p className="text-amber-700">Empty file and no workshop in the file name — pick the workshop above and upload it again so it counts for compliance.</p>}
      {f.rejected?.length > 0 && (
        <div className="max-h-32 overflow-auto border rounded">
          {f.rejected.map((r, i) => <div key={i} className="px-2 py-0.5 border-b last:border-0 text-red-700" data-testid="svc-rejected-line">Line {r.line} · {r.dealer} — {r.reason}</div>)}
        </div>
      )}
    </div>
  );
}

function History({ rows, isAdmin, onChanged }) {
  const rollback = async (r) => {
    if (!window.confirm(`Roll back ${r.file_name}? Its ${r.live_rows} live rows will be removed.`)) return;
    try {
      const { data } = await api.post(`/service/uploads/${r.id}/rollback`);
      toast.success(`Rolled back — ${data.rows_removed} rows removed`);
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="border bg-card rounded-md overflow-auto" data-testid="svc-upload-history">
      <div className="px-4 py-3 border-b flex items-center gap-3"><h3 className="font-heading text-sm font-semibold">Upload history</h3><span className="text-[11px] text-muted-foreground">latest 40 files</span></div>
      <table className="w-full text-xs">
        <thead className="border-b"><tr>{["When", "Report date", "Type", "File", "Workshops", "Data range", "Loaded", "Rejected", "Live", "By", ""].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((h) => (
            <tr key={h.id} className={`border-b last:border-0 ${h.status === "rolled_back" ? "opacity-60" : ""}`} data-testid={`svc-history-row-${h.id}`}>
              <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{fmtWhen(h.created_at)}</td>
              <td className="px-3 py-1.5 whitespace-nowrap">{fmtDay(h.as_of)}</td>
              <td className="px-3 py-1.5"><span className="rounded bg-secondary px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider">{kindLabel(h.kind)}</span></td>
              <td className="px-3 py-1.5 max-w-[220px] truncate" title={h.file_name}>
                {h.file_name}
                {h.status === "rolled_back" && <span className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-secondary text-muted-foreground" data-testid={`svc-rolled-back-${h.id}`}>rolled back</span>}
                {h.superseded && <span className="ml-2 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-secondary text-muted-foreground" title="Every row was replaced by a later upload of the same invoices / ROs" data-testid={`svc-superseded-${h.id}`}>superseded</span>}
              </td>
              <td className="px-3 py-1.5" title={h.dealers.join(", ")}>{fmtDealers(h.dealers, h.group_file) || "—"}</td>
              <td className="px-3 py-1.5 text-muted-foreground whitespace-nowrap">{h.data_from ? `${fmtDay(h.data_from)} → ${fmtDay(h.data_to)}` : "—"}</td>
              <td className="px-3 py-1.5 num">{h.rows_ok}</td>
              <td className={`px-3 py-1.5 num ${h.rows_rejected ? "text-red-600 font-bold" : ""}`}>{h.rows_rejected}</td>
              <td className="px-3 py-1.5 num text-muted-foreground">{h.live_rows}</td>
              <td className="px-3 py-1.5 text-muted-foreground truncate max-w-[140px]">{h.by}</td>
              <td className="px-3 py-1.5 text-right">
                {isAdmin && h.can_rollback && (
                  <Button variant="outline" size="sm" className="h-6 text-[11px] px-2" onClick={() => rollback(h)} data-testid={`svc-rollback-${h.id}`}><Undo2 size={11} className="mr-1" /> Roll back</Button>
                )}
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={11} className="px-3 py-6 text-center text-muted-foreground">No uploads yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

const CHUNK = 2 * 1024 * 1024;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function putChunk(url, blob) {
  let lastErr;
  for (let attempt = 0; attempt < 3; attempt++) {
    try {
      return await api.put(url, blob, { headers: { "Content-Type": "application/octet-stream" }, timeout: 120000 });
    } catch (e) {
      lastErr = e;
      if (e?.response && e.response.status < 500 && e.response.status !== 429) throw e;
      await sleep(800 * (attempt + 1));
    }
  }
  throw lastErr;
}

function Progress({ p }) {
  if (!p) return null;
  const pct = p.phase === "upload" ? Math.round((p.done / p.total) * 100) : p.phase === "processing" ? null : 0;
  const label = p.phase === "create" ? "Preparing upload…"
    : p.phase === "upload" ? `Uploading ${p.name} — part ${p.chunk}/${p.chunks} · file ${p.file + 1} of ${p.files}`
    : p.phase === "processing" ? (p.stage === "reading" ? `Reading ${p.name}…` : p.stage === "loading" ? `Loading ${p.kind_label || p.name} — ${(p.rows || 0).toLocaleString("en-IN")} rows so far · file ${(p.file ?? 0) + 1} of ${p.files || "?"}` : p.stage === "done" ? `Finished ${p.kind_label || p.name} (${(p.rows || 0).toLocaleString("en-IN")} rows)` : "Waiting for the server to start processing…")
    : "";
  return (
    <div className="rounded-md border bg-secondary/40 p-3 text-xs space-y-2" data-testid="svc-upload-progress">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium flex items-center gap-2" data-testid="svc-upload-progress-label"><Loader2 size={13} className="animate-spin" /> {label}</span>
        {pct != null && <span className="num text-muted-foreground">{pct}%</span>}
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        {pct != null ? <div className="h-2 bg-foreground transition-[width] duration-300" style={{ width: `${pct}%` }} /> : <div className="h-2 w-1/3 bg-foreground/70 animate-pulse" />}
      </div>
      <p className="text-[11px] text-muted-foreground">Files are sent in {CHUNK / 1024 / 1024} MB parts and processed on the server in the background — you can keep this page open and watch the progress; nothing times out at the proxy any more.</p>
    </div>
  );
}

export default function ServiceUpload() {
  const { user } = useApp();
  const [meta, setMeta] = useState(null);
  const [files, setFiles] = useState([]);
  const [asOf, setAsOf] = useState("");
  const [workshop, setWorkshop] = useState("auto");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [month, setMonth] = useState(monthOptions()[1]);
  const [complianceKey, setComplianceKey] = useState(0);

  const loadHistory = useCallback(() => api.get("/service/uploads").then(({ data }) => setHistory(data)).catch(() => {}), []);
  useEffect(() => {
    api.get("/service/meta").then(({ data }) => { setMeta(data); setAsOf((v) => v || data.today); }).catch((e) => toast.error(apiError(e)));
    loadHistory();
  }, [loadHistory]);

  const upload = async () => {
    if (!files.length) return;
    setBusy(true);
    setResult(null);
    setProgress({ phase: "create" });
    try {
      const spec = files.map((f) => ({ name: f.name, size: f.size, chunks: Math.max(1, Math.ceil(f.size / CHUNK)) }));
      const { data: job } = await api.post("/service/upload/jobs", { as_of: asOf, workshop: workshop === "auto" ? null : workshop, files: spec });
      const totalChunks = spec.reduce((a, s) => a + s.chunks, 0);
      let done = 0;
      for (let i = 0; i < files.length; i++) {
        for (let c = 0; c < spec[i].chunks; c++) {
          const blob = files[i].slice(c * CHUNK, Math.min(files[i].size, (c + 1) * CHUNK));
          await putChunk(`/service/upload/jobs/${job.job_id}/files/${i}/chunks/${c}`, blob);
          done += 1;
          setProgress({ phase: "upload", file: i, files: files.length, name: files[i].name, chunk: c + 1, chunks: spec[i].chunks, done, total: totalChunks });
        }
      }
      await api.post(`/service/upload/jobs/${job.job_id}/start`);
      setProgress({ phase: "processing", stage: "queued" });
      for (;;) {
        await sleep(1500);
        const { data: st } = await api.get(`/service/upload/jobs/${job.job_id}`);
        setProgress({ phase: "processing", ...st.progress, stage: st.status === "queued" ? "queued" : st.progress?.stage });
        if (st.status === "done") {
          setResult(st.result);
          const okFiles = st.result.files.filter((f) => f.kind).length;
          toast.success(`${okFiles}/${st.result.files.length} files loaded`);
          setFiles([]);
          loadHistory();
          setComplianceKey((k) => k + 1);
          break;
        }
        if (st.status === "failed") throw new Error(st.error || "Processing failed on the server");
      }
    } catch (e) { toast.error(apiError(e), { duration: 10000 }); } finally { setBusy(false); setProgress(null); }
  };

  return (
    <div className="space-y-5" data-testid="service-upload">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight">After-Sales — Daily DMS Upload</h1>

      <div className="border bg-card rounded-md p-5 space-y-4 max-w-4xl">
        <DropZone files={files} setFiles={setFiles} busy={busy} />
        <div className="flex flex-wrap items-center gap-4 text-xs">
          <label className="flex items-center gap-2 text-muted-foreground">
            <span className="font-semibold">Report date</span>
            <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="h-8 border rounded px-2 text-xs" data-testid="svc-asof-date" />
            <span>(the day these reports were pulled — used for compliance)</span>
          </label>
          <label className="flex items-center gap-2 text-muted-foreground">
            <span className="font-semibold">Workshop</span>
            <Select value={workshop} onValueChange={setWorkshop}>
              <SelectTrigger className="h-8 w-[200px] text-xs" data-testid="svc-workshop-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect from file contents</SelectItem>
                {(meta?.workshops || []).map((w) => <SelectItem key={w.code} value={w.code}>{w.code} · {w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </label>
          <Button onClick={upload} disabled={!files.length || busy || !asOf} className="ml-auto" data-testid="svc-upload-btn">{busy ? "Working…" : `Upload ${files.length || ""} file${files.length === 1 ? "" : "s"}`}</Button>
        </div>
        <Progress p={progress} />
        <p className="text-[11px] text-muted-foreground">Upload the four group-wide DMS extracts each morning. Re-uploading is safe: every GST invoice / warranty claim in a file replaces its earlier lines, and each RO / vehicle is updated in place — nothing is double counted. A file covering several workshops counts as received for all of them; the Warranty report is always treated as the single group-level file (a workshop with no claims that day is not penalised) and its approved labour / parts flow into GS revenue by warranty invoice date. Header-only files still count for compliance; their workshop is taken from the selector or the file name.</p>
      </div>

      {result && (
        <div className="space-y-2 max-w-4xl" data-testid="svc-upload-result">
          <h3 className="font-heading text-sm font-semibold">Batch result — report date {fmtDay(result.as_of)}</h3>
          {result.files.map((f, i) => <FileResult key={i} f={f} />)}
          {result.missing_today.length > 0 && (
            <p className="text-xs text-muted-foreground" data-testid="svc-missing-today">
              Still missing for {fmtDay(result.as_of)}: {result.missing_today.map((m) => `${m.code} (${m.missing.map((k) => SVC_KINDS.find((x) => x.key === k)?.short).join("")})`).join(", ")}
            </p>
          )}
        </div>
      )}

      <History rows={history} isAdmin={user?.role === "admin"} onChanged={() => { loadHistory(); setComplianceKey((k) => k + 1); }} />

      <div className="flex items-center gap-3">
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="h-7 w-[130px] text-xs" data-testid="svc-compliance-month"><SelectValue /></SelectTrigger>
          <SelectContent>{monthOptions().map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <ServiceCompliance key={complianceKey} month={month} />
    </div>
  );
}
